import {
	OnOpenChangeData,
	OpenPopoverEvents,
	Popover,
	PopoverSurface,
} from "@fluentui/react-components";
import { MouseEvent, useEffect, useRef } from "react";
import { List } from "@/components/atoms/list";
import { KeyValue } from "@/common/constants/keyValue";
import { SuggestionsPopoverProps } from "./types";

/**
 * Suggestions listed below an input while the user types in it. Focus never leaves the input: the
 * popover does not focus a suggestion when it opens, pressing a suggestion does not take focus, and
 * Tab skips the suggestions. An input that commits its text on blur therefore commits only when the
 * user leaves it.
 */
export const SuggestionsPopover = (props: SuggestionsPopoverProps) => {
	const { target, open, items, onDismiss, positioning, size, classesName } =
		props;
	// Read by the Escape listener, which is attached once per opening.
	const onDismissRef = useRef(onDismiss);
	onDismissRef.current = onDismiss;

	// Escape is typed into the input, outside the popover, so the popover's own Escape handling never
	// sees it.
	useEffect(() => {
		if (!open || !target) {
			return undefined;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== KeyValue.Escape) {
				return;
			}

			// Claims the key, so a dialog around the input stays open.
			event.preventDefault();
			onDismissRef.current();
		};

		target.addEventListener("keydown", onKeyDown);

		return () => target.removeEventListener("keydown", onKeyDown);
	}, [open, target]);

	const onOpenChange = (event: OpenPopoverEvents, data: OnOpenChangeData) => {
		// The input is outside the popover, but a click in it is the user still typing.
		const isInTarget =
			event.target instanceof Node && !!target?.contains(event.target);

		if (!data.open && !isInTarget) {
			onDismiss();
		}
	};

	return (
		<Popover
			inline
			unstable_disableAutoFocus
			open={open}
			onOpenChange={onOpenChange}
			size={size}
			positioning={{ ...positioning, target, position: "below" }}
		>
			<PopoverSurface
				className={classesName?.surface}
				onMouseDown={(event: MouseEvent) => event.preventDefault()}
			>
				<List classesName={classesName?.list} items={items} rowsOutOfTabOrder />
			</PopoverSurface>
		</Popover>
	);
};
